import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "./motion";
import { HOW_IT_WORKS } from "../config/content";

export default function HowItWorks({ onBook }) {
  return (
    <section id="how-it-works" className="py-16 md:py-24" data-testid="how-it-works-section">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>{HOW_IT_WORKS.eyebrow}</Eyebrow>
          <h2 className="font-heading uppercase text-white text-4xl sm:text-5xl leading-none tracking-wide max-w-3xl" data-testid="how-it-works-heading">
            FROM CONFUSED TO <span className="text-[#22C55E]">CONFIDENT</span> IN 4 SIMPLE STEPS
          </h2>
        </Reveal>

        {/* Desktop: horizontal timeline */}
        <div className="hidden md:block mt-16 relative" data-testid="timeline-desktop">
          <svg className="absolute top-6 left-0 w-full h-1" viewBox="0 0 100 2" preserveAspectRatio="none" aria-hidden="true">
            <motion.line
              x1="0" y1="1" x2="100" y2="1"
              stroke="#22C55E" strokeWidth="2" vectorEffect="non-scaling-stroke"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: "easeInOut" }}
            />
          </svg>
          <div className="grid grid-cols-4 gap-8">
            {HOW_IT_WORKS.steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.3, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`timeline-step-${i + 1}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#0F1712] border-2 border-[#22C55E] flex items-center justify-center font-heading text-xl text-[#22C55E] shadow-[0_0_20px_-4px_rgba(34,197,94,0.5)]">
                  {i + 1}
                </div>
                <h3 className="mt-5 font-heading uppercase text-white text-2xl leading-tight tracking-wide">{s.title}</h3>
                <p className="mt-2 text-sm text-[#A7B3AA] leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="md:hidden mt-12 relative pl-10" data-testid="timeline-mobile">
          <svg className="absolute left-[23px] top-2 h-[calc(100%-16px)] w-[2px]" viewBox="0 0 2 100" preserveAspectRatio="none" aria-hidden="true">
            <motion.line
              x1="1" y1="0" x2="1" y2="100"
              stroke="#22C55E" strokeWidth="2" vectorEffect="non-scaling-stroke"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.8, ease: "easeInOut" }}
            />
          </svg>
          <div className="space-y-10">
            {HOW_IT_WORKS.steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
                data-testid={`timeline-step-mobile-${i + 1}`}
              >
                <div className="absolute -left-10 top-0 w-12 h-12 rounded-full bg-[#0F1712] border-2 border-[#22C55E] flex items-center justify-center font-heading text-xl text-[#22C55E]">
                  {i + 1}
                </div>
                <h3 className="font-heading uppercase text-white text-2xl leading-tight tracking-wide pl-4">{s.title}</h3>
                <p className="mt-2 text-sm text-[#A7B3AA] leading-relaxed pl-4">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <Reveal className="mt-14 text-center">
          <button
            onClick={onBook}
            data-testid="how-it-works-book-button"
            className="btn-primary group relative inline-flex items-center gap-3 rounded-full bg-[#22C55E] hover:bg-[#16A34A] text-[#04100A] font-extrabold text-base px-8 py-4 transition-colors overflow-hidden"
          >
            <span className="relative z-10">{HOW_IT_WORKS.cta}</span>
            <ArrowRight className="w-5 h-5 relative z-10 arrow-slide" />
            <span className="shine" aria-hidden="true" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
